/**
 * Messaging data access: wrappers over the generated hooks with module query keys
 * (`['messaging', orgId, …]`, docs/09). Realtime pushes (`message` / `thread` over the shell's
 * SSE stream) invalidate the `messaging` prefix; sends and read markers are also announced to
 * the other windows of the browser through the sync channel (`sync.ts`).
 */
import {
  useMessagingAddEmailParticipant,
  useMessagingCreateThread,
  useMessagingGetThread,
  useMessagingListEmailParticipants,
  useMessagingListMessages,
  useMessagingListRecipients,
  useMessagingListThreads,
  useMessagingMarkRead,
  useMessagingMuteThread,
  useMessagingRemoveEmailParticipant,
  useMessagingSendMessage,
  useMessagingUnreadCount,
  type MessagingListThreadsParams,
} from '@oex/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from '../../app/session';
import { useMessagingSync } from './sync';

export const MODULE = 'messaging';

function useKey() {
  const { activeOrgId } = useSession();
  return (...parts: unknown[]) => [MODULE, activeOrgId, ...parts];
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: [MODULE] });
}

export function useThreads(params: MessagingListThreadsParams, enabled = true) {
  const key = useKey();
  return useMessagingListThreads(params, { query: { queryKey: key('threads', params), enabled } });
}

export function useThread(id: string | null) {
  const key = useKey();
  return useMessagingGetThread(id ?? '', { query: { queryKey: key('thread', id), enabled: Boolean(id) } });
}

export function useMessages(threadId: string | null) {
  const key = useKey();
  return useMessagingListMessages(
    threadId ?? '',
    { limit: 100 },
    {
      query: { queryKey: key('messages', threadId), enabled: Boolean(threadId) },
    },
  );
}

export function useUnread(enabled = true) {
  const key = useKey();
  return useMessagingUnreadCount({ query: { queryKey: key('unread'), enabled } });
}

export function useRecipients(q: string, enabled: boolean) {
  const key = useKey();
  return useMessagingListRecipients(q ? { q } : {}, { query: { queryKey: key('recipients', q), enabled } });
}

export function useEmailParticipants(threadId: string) {
  const key = useKey();
  return useMessagingListEmailParticipants(threadId, {
    query: { queryKey: key('thread', threadId, 'email') },
  });
}

export function useSendMessage() {
  const invalidate = useInvalidate();
  const sync = useMessagingSync();
  return useMessagingSendMessage({
    mutation: {
      onSuccess: (m) => {
        sync.post({ type: 'message', threadId: m.threadId, messageId: m.id });
        return invalidate();
      },
    },
  });
}

export function useCreateThread() {
  const invalidate = useInvalidate();
  const sync = useMessagingSync();
  return useMessagingCreateThread({
    mutation: {
      onSuccess: (t) => {
        sync.post({ type: 'thread', threadId: t.id });
        return invalidate();
      },
    },
  });
}

export function useMarkRead() {
  const invalidate = useInvalidate();
  const sync = useMessagingSync();
  return useMessagingMarkRead({
    mutation: {
      onSuccess: (_r, v) => {
        sync.post({ type: 'read', threadId: v.id, messageId: v.data.lastReadMessageId });
        return invalidate();
      },
    },
  });
}

export function useMute() {
  const invalidate = useInvalidate();
  return useMessagingMuteThread({ mutation: { onSuccess: invalidate } });
}

export function useAddEmailParticipant() {
  const invalidate = useInvalidate();
  return useMessagingAddEmailParticipant({ mutation: { onSuccess: invalidate } });
}

export function useRemoveEmailParticipant() {
  const invalidate = useInvalidate();
  return useMessagingRemoveEmailParticipant({ mutation: { onSuccess: invalidate } });
}
